import { Collapse, Grid, Tab, Tabs } from "@mui/material";
import styles from "./InstitutionCardBasicInfo.module.scss";
import { IconPack } from "Explore/Config/Icons";
import {
  getInstitutionPublicationsByID,
  getInstitutionScholarsByID,
  getInstitutionTopAwardedGrants,
  getInstitutionTopRankingKeywords,
} from "Explore/Services/Institution";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { useEffect, useState } from "react";
import { FacultyCard, LoadingListEntityProfile } from "Explore/Components";
import PublicationItem from "Explore/Components/PublicationItem";
import { Empty } from "antd";
import AwardItem from "Explore/Components/AwardItem";
import KeywordCardInfo from "Explore/Components/KeywordCardInfo";
import useAuth from "Hooks/useAuth";
import LoginRequiredSection from "Explore/Components/LoginRequired";

const MAP_TAB_VALUES = Object.freeze({
  TOP_RANKING_KEYWORDS: 1,
  TOP_SCHOLARS: 2,
  PUBLICATIONS: 3,
  GRANTS: 4,
});

const TAB_VALUES = [
  {
    label: "Top Ranking Keywords",
    value: MAP_TAB_VALUES.TOP_RANKING_KEYWORDS,
    icon: IconPack.keyword,
  },
  {
    label: "Top Scholars",
    value: MAP_TAB_VALUES.TOP_SCHOLARS,
    icon: IconPack.unit_faculty_members,
  },
  {
    label: "Notable Scholarly Works",
    value: MAP_TAB_VALUES.PUBLICATIONS,
    icon: IconPack.unit_publications,
  },
  {
    label: "Notable Federal Grants",
    value: MAP_TAB_VALUES.GRANTS,
    icon: IconPack.grant,
  },
];

const InstitutionCardBasicInfo = ({ data }) => {
  const { userTypeConfig } = useAuth({ entityType: "INSTITUTION" });
  const { isTablet } = useDeviceCheck();
  const [tabValue, setTabValue] = useState(false);
  const [publications, setPublications] = useState({
    data: [],
    isLoading: true,
  });
  const [grants, setGrants] = useState({ data: [], isLoading: true });
  const [topScholars, setTopScholars] = useState({
    data: [],
    isLoading: true,
  });
  const [topRankingKeywords, setTopRankingKeywords] = useState({
    data: [],
    isLoading: true,
  });

  useEffect(() => {
    if (tabValue === MAP_TAB_VALUES.PUBLICATIONS && publications.isLoading) {
      getInstitutionPublicationsByID({
        id: data.id,
        unit: null,
        keywords: [],
        verified: true,
        startYear: null,
        endYear: null,
        page: 0,
        rowsPerPage: 3,
      }).then((res) => {
        setPublications({ data: res.results, isLoading: false });
      });
    }
  }, [tabValue, publications.isLoading]);
  useEffect(() => {
    if (tabValue === MAP_TAB_VALUES.GRANTS && grants.isLoading) {
      getInstitutionTopAwardedGrants({
        id: data.id,
        unit_name: null,
        keywords: [],
        page: 0,
        rowsPerPage: 3,
        fromYear: null,
        toYear: null,
        order_by: "amount_awarded",
        order: "desc",
      }).then((res) => {
        setGrants({ data: res.awards, isLoading: false });
      });
    }
  }, [tabValue, grants.isLoading]);

  useEffect(() => {
    if (tabValue === MAP_TAB_VALUES.TOP_SCHOLARS && topScholars.isLoading) {
      getInstitutionScholarsByID({
        id: data.id,
        query: null,
        page: 0,
        rowsPerPage: 10,
        startYear: null,
        endYear: null,
        keywords: [],
        department: null,
        order_by: "h_index",
      }).then((res) => {
        setTopScholars({ data: res.authors, isLoading: false });
      });
    }
  }, [tabValue, topScholars.isLoading]);
  useEffect(() => {
    if (
      tabValue === MAP_TAB_VALUES.TOP_RANKING_KEYWORDS &&
      topRankingKeywords.isLoading
    ) {
      if (userTypeConfig?.ui_components?.topKeywords)
        getInstitutionTopRankingKeywords({ institution_id: data.id }).then(
          (res) => {
            setTopRankingKeywords({
              data: res.top_ranking_keywords,
              isLoading: false,
            });
          }
        );
      else setTopRankingKeywords({ data: [], isLoading: false });
    }
  }, [tabValue, topRankingKeywords.isLoading]);

  const handleChange = (_, newValue) => {
    setTabValue(newValue);
  };

  return (
    <div className={styles["basic-info"]}>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        classes={{
          root: styles["tabs"],
          indicator: styles["tabs--indicator"],
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {TAB_VALUES.map(({ label, value, icon }) => (
          <Tab
            key={value}
            label={label}
            value={value}
            icon={icon}
            iconPosition="start"
            onClick={() => value === tabValue && setTabValue(false)}
            classes={{
              root: styles["tabs--item"],
              selected: styles["selected"],
            }}
          />
        ))}
      </Tabs>

      <Collapse
        in={tabValue != false}
        classes={{ wrapperInner: styles["tabs--content"] }}
      >
        {tabValue === MAP_TAB_VALUES.PUBLICATIONS && (
          <LoadingListEntityProfile
            isLoading={publications.isLoading}
            component={
              publications.data?.length > 0 ? (
                publications.data.map((item, index) => {
                  return <PublicationItem key={index} data={item} />;
                })
              ) : (
                <Empty description="No Publication Found." />
              )
            }
            componentNum={3}
            avatar={true}
          />
        )}
        {tabValue === MAP_TAB_VALUES.GRANTS && (
          <LoadingListEntityProfile
            isLoading={grants.isLoading}
            component={
              grants.data?.length > 0 ? (
                grants.data.map((item, index) => {
                  return <AwardItem key={index} data={item} />;
                })
              ) : (
                <Empty description="No Award Found." />
              )
            }
            componentNum={3}
            avatar={true}
          />
        )}
        {tabValue === MAP_TAB_VALUES.TOP_SCHOLARS && (
          <LoadingListEntityProfile
            isLoading={topScholars.isLoading}
            component={
              topScholars.data?.length > 0 ? (
                topScholars.data.map((item, index) => {
                  return <FacultyCard simpleMode key={index} data={item} />;
                })
              ) : (
                <Empty description="No Award Found." />
              )
            }
            componentNum={3}
            avatar={true}
          />
        )}
        {tabValue === MAP_TAB_VALUES.TOP_RANKING_KEYWORDS &&
          (userTypeConfig?.ui_components?.topKeywords ? (
            <LoadingListEntityProfile
              isLoading={topRankingKeywords.isLoading}
              component={
                topRankingKeywords.data?.length > 0 ? (
                  <Grid container spacing="24px">
                    {topRankingKeywords.data.slice(0, 4).map((item, index) => {
                      return (
                        <Grid item xs={6} key={index}>
                          <KeywordCardInfo data={item} />
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Empty description="No Keyword Found." />
                )
              }
              componentNum={3}
              avatar={true}
            />
          ) : (
            <LoginRequiredSection />
          ))}
      </Collapse>
    </div>
  );
};

export default InstitutionCardBasicInfo;
